import React, { useState, useEffect, useCallback } from 'react';
import { useOrg } from '../context/OrgContext';
import { usePermissions } from '../components/PermissionGuard';
import { PageHeader } from '../components/PageHeader';
import * as usageSummaryApi from '../api/usageSummary';

export function UsageReporting() {
  const { selectedOrg } = useOrg();
  const { canReadAssets } = usePermissions();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({
    from: '',
    to: '',
    siteId: '',
    programId: ''
  });

  const loadSummary = useCallback(async () => {
    if (!selectedOrg || !canReadAssets) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const activeFilters = {};
      if (filters.from) activeFilters.from = filters.from;
      if (filters.to) activeFilters.to = filters.to;
      if (filters.siteId) activeFilters.siteId = filters.siteId;
      if (filters.programId) activeFilters.programId = filters.programId;
      
      const data = await usageSummaryApi.getUsageSummary(selectedOrg.id, activeFilters);
      setSummary(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [selectedOrg, canReadAssets, filters]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const handleExport = async () => {
    if (!selectedOrg || exporting) return;
    setExporting(true);
    try {
      const activeFilters = {};
      if (filters.from) activeFilters.from = filters.from;
      if (filters.to) activeFilters.to = filters.to;
      if (filters.siteId) activeFilters.siteId = filters.siteId;
      if (filters.programId) activeFilters.programId = filters.programId;
      
      const job = await usageSummaryApi.createUsageExport(selectedOrg.id, activeFilters);
      alert(`Export created: ${job.filename}\nRecords: ${job.recordCount}\nChecksum: ${job.checksum}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setExporting(false);
    }
  };

  if (!canReadAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.accessDenied}>
          <h2>Access Denied</h2>
          <p>You do not have permission to view usage reports.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <PageHeader
        title="Usage Reporting"
        subtitle="View usage summaries and export data"
      />

      <div style={styles.filtersPanel}>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>From</label>
          <input
            type="date"
            value={filters.from}
            onChange={(e) => setFilters(prev => ({ ...prev, from: e.target.value }))}
            style={styles.filterInput}
            data-testid="filter-from"
          />
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>To</label>
          <input
            type="date"
            value={filters.to}
            onChange={(e) => setFilters(prev => ({ ...prev, to: e.target.value }))}
            style={styles.filterInput}
            data-testid="filter-to"
          />
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Site</label>
          <select
            value={filters.siteId}
            onChange={(e) => setFilters(prev => ({ ...prev, siteId: e.target.value }))}
            style={styles.filterSelect}
            data-testid="filter-site"
          >
            <option value="">All Sites</option>
            <option value="site-1">Downtown Station</option>
            <option value="site-2">Highway Hub</option>
            <option value="site-3">Mall Charging</option>
          </select>
        </div>
        <div style={styles.filterGroup}>
          <label style={styles.filterLabel}>Program</label>
          <select
            value={filters.programId}
            onChange={(e) => setFilters(prev => ({ ...prev, programId: e.target.value }))}
            style={styles.filterSelect}
            data-testid="filter-program"
          >
            <option value="">All Programs</option>
            <option value="program-nevi">NEVI</option>
            <option value="program-fleet">Fleet</option>
          </select>
        </div>
        <button
          onClick={handleExport}
          disabled={exporting}
          style={styles.exportButton}
          data-testid="export-button"
        >
          {exporting ? 'Exporting...' : 'Export CSV'}
        </button>
      </div>

      {error && (
        <div style={styles.error}>{error}</div>
      )}

      {loading ? (
        <div style={styles.loading}>Loading usage data...</div>
      ) : summary && (
        <>
          <div style={styles.summaryCards}>
            <div style={styles.summaryCard} data-testid="total-sessions-card">
              <div style={styles.cardValue}>{summary.totals.totalSessions.toLocaleString()}</div>
              <div style={styles.cardLabel}>Total Sessions</div>
            </div>
            <div style={styles.summaryCard} data-testid="total-energy-card">
              <div style={styles.cardValue}>{summary.totals.totalEnergyKwh.toLocaleString(undefined, { maximumFractionDigits: 1 })}</div>
              <div style={styles.cardLabel}>Total Energy (kWh)</div>
            </div>
            <div style={styles.summaryCard} data-testid="record-count-card">
              <div style={styles.cardValue}>{summary.totals.recordCount}</div>
              <div style={styles.cardLabel}>Records</div>
            </div>
          </div>

          <div style={styles.chartsRow}>
            <div style={styles.chartPanel}>
              <h3 style={styles.chartTitle}>Usage by Site</h3>
              <div style={styles.barChart} data-testid="site-chart">
                {summary.bySite.map(site => (
                  <div key={site.siteId} style={styles.barRow}>
                    <div style={styles.barLabel}>{site.siteName}</div>
                    <div style={styles.barContainer}>
                      <div 
                        style={{
                          ...styles.bar,
                          width: `${Math.min(100, (site.energyKwh / Math.max(...summary.bySite.map(s => s.energyKwh))) * 100)}%`
                        }}
                      />
                    </div>
                    <div style={styles.barValue}>{site.energyKwh.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh</div>
                  </div>
                ))}
              </div>
            </div>

            <div style={styles.chartPanel}>
              <h3 style={styles.chartTitle}>Usage by Month</h3>
              <div style={styles.barChart} data-testid="month-chart">
                {summary.byMonth.map(month => (
                  <div key={month.month} style={styles.barRow}>
                    <div style={styles.barLabel}>{month.month}</div>
                    <div style={styles.barContainer}>
                      <div 
                        style={{
                          ...styles.bar,
                          background: '#48bb78',
                          width: `${Math.min(100, (month.energyKwh / Math.max(...summary.byMonth.map(m => m.energyKwh))) * 100)}%`
                        }}
                      />
                    </div>
                    <div style={styles.barValue}>{month.energyKwh.toLocaleString(undefined, { maximumFractionDigits: 0 })} kWh</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={styles.tableSection}>
            <h3 style={styles.sectionTitle}>Detailed Records</h3>
            <div style={styles.tableContainer}>
              <table style={styles.table} data-testid="records-table">
                <thead>
                  <tr>
                    <th style={styles.th}>Site</th>
                    <th style={styles.th}>Asset</th>
                    <th style={styles.th}>Program</th>
                    <th style={styles.th}>Date</th>
                    <th style={styles.th}>Sessions</th>
                    <th style={styles.th}>Energy (kWh)</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.records.map((record, i) => (
                    <tr key={i} style={styles.tr}>
                      <td style={styles.td}>{record.siteName}</td>
                      <td style={styles.td}>{record.assetId}</td>
                      <td style={styles.td}>{record.programId}</td>
                      <td style={styles.td}>{record.date}</td>
                      <td style={styles.td}>{record.sessionCount}</td>
                      <td style={styles.td}>{record.energyKwh.toLocaleString(undefined, { maximumFractionDigits: 1 })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const styles = {
  container: { padding: '2rem', maxWidth: '1400px', margin: '0 auto' },
  accessDenied: { padding: '3rem', textAlign: 'center', background: '#fff', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  filtersPanel: { display: 'flex', gap: '1rem', alignItems: 'flex-end', marginBottom: '1.5rem', flexWrap: 'wrap', background: 'white', padding: '1rem', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  filterLabel: { fontSize: '0.75rem', fontWeight: '500', color: '#4a5568' },
  filterInput: { padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem' },
  filterSelect: { padding: '0.5rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.875rem', background: 'white', minWidth: '140px' },
  exportButton: { padding: '0.5rem 1rem', borderRadius: '6px', border: '1px solid #3182ce', background: '#3182ce', color: 'white', fontSize: '0.875rem', cursor: 'pointer', marginLeft: 'auto' },
  error: { padding: '1rem', background: '#fed7d7', color: '#c53030', borderRadius: '8px', marginBottom: '1rem' },
  loading: { padding: '2rem', textAlign: 'center', color: '#718096' },
  summaryCards: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1rem', marginBottom: '1.5rem' },
  summaryCard: { background: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', textAlign: 'center' },
  cardValue: { fontSize: '2rem', fontWeight: '700', color: '#2d3748' },
  cardLabel: { fontSize: '0.875rem', color: '#718096', marginTop: '0.25rem' },
  chartsRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' },
  chartPanel: { background: 'white', borderRadius: '8px', padding: '1.5rem', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  chartTitle: { margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '600', color: '#2d3748' },
  barChart: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  barRow: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  barLabel: { width: '120px', fontSize: '0.75rem', color: '#4a5568', textAlign: 'right' },
  barContainer: { flex: 1, height: '20px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' },
  bar: { height: '100%', background: '#3182ce', borderRadius: '4px', transition: 'width 0.3s' },
  barValue: { width: '100px', fontSize: '0.75rem', color: '#4a5568' },
  tableSection: { marginTop: '1.5rem' },
  sectionTitle: { margin: '0 0 1rem 0', fontSize: '1rem', fontWeight: '600', color: '#2d3748' },
  tableContainer: { background: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '0.75rem 1rem', background: '#f7fafc', borderBottom: '1px solid #e2e8f0', fontWeight: '600', fontSize: '0.75rem', color: '#4a5568', textTransform: 'uppercase' },
  tr: { borderBottom: '1px solid #e2e8f0' },
  td: { padding: '0.75rem 1rem', fontSize: '0.875rem' }
};
